import { useRef, type ReactNode } from "react";
import { CheckCircle2, Download, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
          {step}
        </div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
        {step}
      </div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}


function InfoCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Install Node.js step                                                      */
/* -------------------------------------------------------------------------- */

export function InstallNodeStep() {
  const { selectedPlatform } = useOnboarding();
  const platform = selectedPlatform ?? "mac";
  const isMac = platform === "mac";

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What is Node.js?">
        <p className="text-sm leading-6 text-muted-foreground">
          Node.js is the engine that runs the setup tools SpecTree needs. When you install Node.js, <strong className="text-foreground/90">npm</strong> comes with it, so one install gives you both.
        </p>
        <InfoCard>
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-emerald-500/10 text-emerald-400">
              <Download className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground">Node.js</p>
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  v20+
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {isMac
                  ? "Download it from nodejs.org, or use nvm if you already use that tool."
                  : "Download the installer from nodejs.org and follow the setup screens."}
              </p>
            </div>
          </div>
        </InfoCard>
        {isMac && (
          <CopyableTerminal title="Terminal (if you use nvm)" copyText="nvm install 20">
            <p><span className="text-foreground/45">$ </span>nvm install 20</p>
          </CopyableTerminal>
        )}
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Install Node.js">
        <p className="text-sm leading-6 text-muted-foreground">
          {isMac
            ? "The easiest way is to download the macOS installer from the official site. If you already use nvm, that works too."
            : "Download the Windows installer (.msi) from the official site. The default options are fine and usually set everything up for you."}
        </p>
        <a
          href="https://nodejs.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.1]"
        >
          Open nodejs.org
          <ExternalLink className="h-4 w-4" />
        </a>
        <p className="text-xs leading-5 text-muted-foreground">
          Download the <strong className="text-foreground/80">LTS</strong> version (the stable one), v20 or newer. After install, close and reopen your terminal.
        </p>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Verify the installation">
        <p className="text-sm leading-6 text-muted-foreground">
          Open your terminal and run these two commands. You should see version numbers for both Node.js and npm. If one says it cannot be found, reopen your terminal and try again.
        </p>
        <div className="space-y-3">
          <CopyableTerminal title="Check Node.js" copyText="node --version">
            <div className="space-y-1.5">
              <p>
                <span className="text-foreground/45">$ </span>
                <span className="text-foreground">node --version</span>
              </p>
              <p className="text-emerald-400">v20.18.0</p>
            </div>
          </CopyableTerminal>
          <CopyableTerminal title="Check npm" copyText="npm --version">
            <div className="space-y-1.5">
              <p>
                <span className="text-foreground/45">$ </span>
                <span className="text-foreground">npm --version</span>
              </p>
              <p className="text-emerald-400">10.8.2</p>
            </div>
          </CopyableTerminal>
        </div>
        <InfoCard>
          <div className="flex items-start gap-3">
            <CheckCircle2 className={cn("mt-0.5 h-4 w-4 shrink-0 text-emerald-400")} />
            <p className="text-sm leading-6 text-muted-foreground">
              If both commands print version numbers, you are ready. The exact numbers can be different.
            </p>
          </div>
        </InfoCard>
      </NumberedInstructionLast>
    </div>
  );
}
